import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { cn, formatDate } from "@/lib/utils";
import { Megaphone } from "lucide-react";

interface OfferRow {
  id: number;
  name: string;
  status: string;
  visibility: string;
  conversionMethod: string;
  createdAt: string;
}

const STATUS_COLORS: Record<string, string> = {
  active: "bg-emerald-50 text-emerald-700",
  paused: "bg-amber-50 text-amber-700",
  inactive: "bg-gray-100 text-gray-500",
};

export function AdvOffersPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["adv-portal", "offers"],
    queryFn: async () => {
      const res = await api.get("/v1/portal/advertiser/offers");
      if (!res.ok) throw new Error("Failed to load offers");
      return (await res.json()).data as OfferRow[];
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Offers</h1>
        <p className="text-sm text-gray-500 mt-1">Offers you own on this network</p>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse h-16" />)}
        </div>
      ) : !data?.length ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <Megaphone className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">You have no offers yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs">ID</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs">Name</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs">Status</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs">Visibility</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs">Method</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs">Created</th>
              </tr>
            </thead>
            <tbody>
              {data.map((offer) => (
                <tr key={offer.id} className="border-t border-gray-50 hover:bg-gray-50/50">
                  <td className="py-3 px-4 text-gray-400 text-xs">{offer.id}</td>
                  <td className="py-3 px-4">
                    <Link
                      to="/portal/advertiser/offers/$offerId"
                      params={{ offerId: String(offer.id) }}
                      className="text-sm font-medium text-gray-900 hover:text-blue-700 transition-colors"
                    >
                      {offer.name}
                    </Link>
                  </td>
                  <td className="py-3 px-4">
                    <span className={cn("text-[10px] font-medium px-1.5 py-0.5 rounded uppercase", STATUS_COLORS[offer.status] || "bg-gray-100 text-gray-500")}>
                      {offer.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-gray-500 text-xs capitalize">{offer.visibility}</td>
                  <td className="py-3 px-4 text-gray-500 text-xs">{offer.conversionMethod}</td>
                  <td className="py-3 px-4 text-gray-400 text-xs">{formatDate(offer.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
